import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ApiError } from '@/core/api';
import { useSignContract } from '../queries';
import type { Contract } from '../types';

/**
 * Signing provisions the delivery project, whose name defaults to the contract
 * title — often far too long to live with. Ask for a short alternative first.
 *
 * When the contract already points at a project, signing reuses it (the server
 * never replaces an assigned project), so there's nothing to name.
 */
export function SignContractDialog({
  open,
  onOpenChange,
  contract,
  onSigned,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contract: Contract;
  onSigned: (projectId: number) => void;
}) {
  const sign = useSignContract();
  const reusesProject = contract.project != null;

  // Only promise what will actually happen: a manually-entered contract has no
  // proposal to accept and no deal to win, so listing those would be wrong.
  const effects = [
    contract.deal != null ? 'wins the deal' : null,
    contract.proposal != null ? 'accepts the parent proposal' : null,
    reusesProject ? 'keeps the linked project' : 'provisions the delivery project',
  ].filter((e): e is string => e !== null);
  const effectSentence =
    effects.length > 1
      ? `${effects.slice(0, -1).join(', ')} and ${effects.at(-1)}`
      : effects[0];

  const [name, setName] = useState(contract.title ?? '');
  const [error, setError] = useState<string | null>(null);

  async function handleSign() {
    setError(null);
    try {
      const project = await sign.mutateAsync({
        id: contract.id,
        // Ignored server-side when a project is already linked.
        project_name: reusesProject ? undefined : name.trim(),
      });
      onOpenChange(false);
      onSigned(project.id);
    } catch (e) {
      const err = e as ApiError;
      setError(
        err?.fieldErrors ? Object.values(err.fieldErrors).flat().join(' ') : err?.message,
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Sign contract</DialogTitle>
          <DialogDescription>
            {`Signing marks the contract signed and ${effectSentence}.`}
          </DialogDescription>
        </DialogHeader>

        {reusesProject ? (
          <p className="text-muted-foreground text-sm">
            No project name needed — this contract already points at one.
          </p>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="sign-project-name">Project name</Label>
            <Input
              id="sign-project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Short name for the project"
            />
            <p className="text-muted-foreground text-xs">
              Defaults to the contract title — shorten it if you like.
            </p>
          </div>
        )}

        {error ? <p className="text-destructive text-sm">{error}</p> : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sign.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSign}
            disabled={sign.isPending || (!reusesProject && name.trim() === '')}
          >
            {sign.isPending ? 'Signing…' : 'Sign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
